import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, EMPTY, Subject, takeUntil } from 'rxjs';
import {
  AuthorizedUser,
  hasAnyOfThoseRoles,
  User,
  UserRole,
} from '../../../../shared/model/user.model';
import { UserHttpService } from '../../../../shared/service/user-http.service';
import { DataHttpService } from '../../../../shared/service/data-http.service';
import { CxSnackbarService } from '@bbraun/cortex/snackbar';
import { OrderStateEnum } from '../../../../shared/model/order-state.enum';
import { TranslateService } from '@ngx-translate/core';
import { DocumentHttpService } from '../../../../shared/service/document-http.service';
import { TemplateHttpService } from '../../../../template/template-overview/template-http.service';
import { RemoteAPIHttpService } from '../../../../shared/service/remoteAPI-http.service';
import {
  CrmRecord,
  getAddress,
} from '../../../../shared/model/remoteAPI.model';
import { Order } from '../../../order.model';

export const reasons: string[] = [
  'Neuer Hygieneplan',
  'Nachbearbeitung',
  'Folgebegehung',
];

@Component({
  selector: 'hpm-general-form',
  templateUrl: './general-form.component.html',
  styleUrl: './general-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralFormComponent implements OnInit, OnDestroy {
  @Input() order!: Order;
  @Output() orderChange: EventEmitter<string | undefined> = new EventEmitter<
    string | undefined
  >();
  private onDestroy$: Subject<void> = new Subject<void>();
  orderForm!: FormGroup;
  specialties: string[] = [];
  users: User[] = [];
  assignedTo: User | undefined;
  isInspectionDateValid = false;
  isInspectionProtocolAttached = false;
  isFormDisabled = false;
  currentUser: AuthorizedUser | null = null;

  protected readonly reasons = reasons;
  plans: string[] = [];

  showCustomerTransactionNumberField = false;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserHttpService,
    private dataService: DataHttpService,
    private templateHttpService: TemplateHttpService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private snackbarService: CxSnackbarService,
    private documentHttpService: DocumentHttpService,
    private remoteAPIService: RemoteAPIHttpService,
  ) {
    this.orderForm = this.formBuilder.group(
      {
        customerTransactionNumber: [''],
        customer: ['', Validators.required],
        contact: ['', Validators.required],
        telephone: [],
        email: [],
        hygieneRepresentative: [],
        address: [
          { value: '', disabled: true, validators: Validators.required },
        ],
        medicalPractice: [[], { updateOn: 'change' }],
        reason: [],
        commissionedBy: [],
        status: ['', { updateOn: 'change' }],
        priority: ['', { updateOn: 'change' }],
        priorityReason: [''],
        assignedTo: [],
        specialtyField: [[], Validators.required],
        requiredHygienePlans: [[], Validators.required],
      },
      { updateOn: 'blur' },
    );
  }

  ngOnInit(): void {
    this.prepareForm();
    this.saveOnValueChanges();
    this.loadData();
    this.loadUserlist();
    this.patchOrderToForm();
    this.addPriorityReasonValidator();

    this.orderForm
      .get('assignedTo')
      ?.valueChanges.pipe()
      .subscribe((assignedTo) => {
        this.enableFormOnUser(assignedTo);
      });
  }

  private addPriorityReasonValidator(): void {
    const priorityControl = this.orderForm.get('priority');
    const priorityReasonControl = this.orderForm.get('priorityReason');
    priorityControl?.valueChanges.subscribe((priority) => {
      if (priority) {
        // for setting validations
        priorityReasonControl?.setValidators(Validators.required);
      } else {
        priorityReasonControl?.clearValidators();
      }
      priorityReasonControl?.updateValueAndValidity();
    });

    // the validation depends on the status of the loaded data, after loading the trigger must be set correctly once.
    if (priorityControl?.getRawValue() === true) {
      priorityReasonControl?.setValidators(Validators.required);
      priorityReasonControl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private saveOnValueChanges(): void {
    this.orderForm.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        const newOrder = this.orderForm.value;
        Object.assign(this.order, newOrder);
        this.orderChange.emit();
      });
  }

  private loadData(): void {
    this.dataService
      .getSpecialties()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((specialties) => {
        this.specialties = specialties;
        this.specialties.sort((a, b) => a.localeCompare(b, 'de'));
        this.cdr.detectChanges();
      });

    this.templateHttpService
      .getNames()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((templateNames) => {
        this.plans = templateNames;
        this.plans.sort((a, b) => a.localeCompare(b, 'de'));
        this.cdr.detectChanges();
      });
  }

  private loadUserlist(): void {
    this.userService
      .getUserList()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((userList) => {
        this.users = userList;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  compareUser(user1: User, user2: User): boolean {
    return user2?.id === user1?.id;
  }

  private patchOrderToForm(): void {
    this.isInspectionDateValid = this.order.inspectionDate !== null;
    this.orderForm.patchValue(this.order, { emitEvent: false });
  }

  protected prepareForm(): void {
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((currentUser) => {
        this.currentUser = currentUser;

        if (currentUser !== null) {
          this.showTransactionFieldOnUser();

          this.enableFormOnUser(this.order.assignedTo);
        }
      });
  }

  private showTransactionFieldOnUser(): void {
    if (
      hasAnyOfThoseRoles(this.currentUser, [
        UserRole.OFFICE_SERVICE,
        UserRole.FIELD_SERVICE,
        UserRole.ADMIN,
      ])
    ) {
      this.showCustomerTransactionNumberField = true;
    }
  }

  private enableFormOnUser(assignedUser: User): void {
    if (this.currentUser?.id !== assignedUser?.id) {
      this.disableForm();
    } else {
      this.enableForm();
      this.enableAddressFieldForRetailPartner();
    }
    this.enableAssignedToOnUser();
    this.cdr.detectChanges();
  }

  private enableForm(): void {
    this.orderForm.enable({ emitEvent: false });
    this.isFormDisabled = false;
  }

  private disableForm(): void {
    this.orderForm.disable({ emitEvent: false });
    this.isFormDisabled = true;
  }

  private enableAddressFieldForRetailPartner(): void {
    if (!hasAnyOfThoseRoles(this.currentUser, [UserRole.RETAIL_PARTNER])) {
      this.orderForm.controls['address'].disable({ emitEvent: false });
    } else {
      this.orderForm.controls['address'].enable({ emitEvent: false });
      this.orderForm.controls['address'].addValidators(Validators.required);
    }
  }

  private enableAssignedToOnUser(): void {
    if (
      hasAnyOfThoseRoles(this.currentUser, [
        UserRole.OFFICE_SERVICE,
        UserRole.ADMIN,
      ])
    ) {
      this.orderForm.controls['assignedTo'].enable({
        emitEvent: false,
      });
    } else {
      this.orderForm.controls['assignedTo'].disable({
        emitEvent: false,
      });
    }
  }

  private showValidationErrors(): void {
    this.translateService
      .get('ORDER_DETAIL.GENERAL.FORM.LABEL.STATUS_ERROR_MESSAGE')
      .subscribe((res: string) => {
        this.snackbarService.error(res, 'close');
      });
  }

  validateForm(): boolean {
    this.orderForm.markAllAsTouched();
    return !this.orderForm.invalid;
  }

  validateInspectionDateAndProtocolDocuments(): boolean {
    return this.isInspectionDateValid && this.isInspectionProtocolAttached;
  }

  isStatusChangeValid(): boolean {
    return (
      this.validateForm() && this.validateInspectionDateAndProtocolDocuments()
    );
  }

  onStatusChange(event: {
    oldStatus: OrderStateEnum;
    newStatus: OrderStateEnum;
    assignedTo: User;
  }): void {
    const statusChangeNeedValidation =
      event.oldStatus === OrderStateEnum.IN_CAPTURE &&
      event.newStatus === OrderStateEnum.IN_INCOMING_INSPECTION;
    if (statusChangeNeedValidation) {
      this.documentHttpService
        .getProtocolDocumentsForOrder(this.order.id!)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((documentList) => {
          this.isInspectionProtocolAttached = documentList.length > 0;
          if (this.isStatusChangeValid()) {
            this.setStatus(event.newStatus, event.assignedTo);
          } else {
            this.showValidationErrors();
            this.orderForm.controls['status'].setValue(event.oldStatus, {
              emitEvent: false,
            });
          }
        });
    } else {
      this.setStatus(event.newStatus, event.assignedTo);
    }
    this.cdr.detectChanges();
  }

  setStatus(selectedStatus: OrderStateEnum, assignedTo: User): void {
    this.order.status = selectedStatus;
    this.orderForm.patchValue({
      status: selectedStatus,
      assignedTo,
    });
  }

  lookupCRMData(): void {
    const id = this.orderForm.get('customerTransactionNumber')?.value;
    if (id) {
      this.remoteAPIService
        .getCaseFromCRM(id)
        .pipe(
          takeUntil(this.onDestroy$),
          catchError(() => {
            this.orderForm.get('customerTransactionNumber')?.setErrors({
              customError: true,
              errorMessage: 'Case ID not found',
            });
            return EMPTY;
          }),
        )
        .subscribe((record) => {
          if (record) {
            this.copyCRMDataIntoForm(record);
          }
        });
    }
  }

  private copyCRMDataIntoForm(record: CrmRecord): void {
    const accountName = record?.account?.name;
    const customer = this.orderForm.get('customer')?.value;
    if (!customer && accountName) {
      this.orderForm
        .get('customer')
        ?.setValue(accountName, { emitEvent: false });
    }
    const phone = record?.contact?.phone;
    if (phone) {
      this.orderForm.get('telephone')?.setValue(phone, { emitEvent: false });
    }
    const email = record?.contact?.email;
    if (email) {
      this.orderForm.get('email')?.setValue(email, { emitEvent: false });
    }
    const academicTitle = record?.contact?.academicTitle;
    const name = record?.contact?.name;
    if (name) {
      let formalName = name;
      if (academicTitle) {
        formalName = academicTitle + ' ' + name;
      }
      this.orderForm.get('contact')?.setValue(formalName, { emitEvent: false });
    }

    const address = getAddress(record);

    if (address.length) {
      const enabled = this.orderForm.get('address')?.enabled;
      this.orderForm.get('address')?.enable({ emitEvent: false });
      this.orderForm.get('address')?.setValue(address, { emitEvent: false });
      this.order.address = address;
      if (!enabled) {
        this.orderForm.get('address')?.disable({ emitEvent: false });
      }
    }

    const medicalSpecialities = record?.account?.medicalSpecialities;

    if (medicalSpecialities) {
      this.orderForm
        .get('specialtyField')
        ?.patchValue(medicalSpecialities, { emitEvent: false });
    }
    this.orderForm.updateValueAndValidity({ emitEvent: true });
  }
}
